import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { Phone, Mail, MapPin, AlertCircle, User, ChevronLeft, ChevronRight, Microchip, Shield, Syringe } from 'lucide-react';
import { toast } from 'sonner';

import { fetchOwnedPetByUUID } from '@/api/ownedPets';
import { ANIMAL_TYPES, ANIMAL_AGES, PET_COLORS, PET_GENDER } from '@/constants';
import { LostAndFoundSkeleton } from '@/components/Skeletons';
import { NbButton, NbCard, NbChip } from '@/components/Neobrut';

export const PublicPetProfile = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const [pet, setPet] = useState<OwnedPet | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const loadPet = async () => {
      if (!uuid) {
        setError('UUID no proporcionado');
        setIsLoading(false);
        return;
      }
      try {
        const petData = await fetchOwnedPetByUUID(uuid);
        if (!petData || !petData.owner || !petData.owner.email) {
          throw new Error('Datos de mascota incompletos');
        }
        setPet(petData);
        setIsLoading(false);
      } catch (err) {
        console.error('Error loading pet:', err);
        setError('Mascota no encontrada');
        setIsLoading(false);
        toast.error('No se pudo cargar el perfil de la mascota');
      }
    };
    loadPet();
  }, [uuid]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <LostAndFoundSkeleton />
      </div>
    );
  }

  if (error || !pet || !pet.owner) {
    return (
      <div className="mx-auto flex w-full max-w-md flex-col items-center gap-3 px-4 py-16 text-center">
        <AlertCircle size={48} />
        <h1 className="font-display text-[26px] leading-tight">Perfil no encontrado</h1>
        <p className="text-[13px] font-medium opacity-70">
          {error ?? 'No se pudo encontrar el perfil de esta mascota.'}
        </p>
      </div>
    );
  }

  const hasGallery = Boolean(pet.gallery && pet.gallery.length > 0);
  const nextImage = () =>
    setCurrentImageIndex((i) => (pet.gallery ? (i + 1) % pet.gallery.length : i));
  const prevImage = () =>
    setCurrentImageIndex((i) =>
      pet.gallery ? (i - 1 + pet.gallery.length) % pet.gallery.length : i,
    );

  return (
    <div className="mx-auto w-full max-w-2xl lg:max-w-3xl pb-10">
      <div className="px-4 pb-2 pt-2">
        <NbChip fill="urgent">
          ⚠ Si estás viendo este perfil, encontraste a una mascota
        </NbChip>
      </div>

      <div className="px-4">
        <NbCard className="p-0">
          {/* Gallery */}
          {hasGallery && (
            <div
              className="relative aspect-square w-full overflow-hidden"
              style={{ borderBottom: '3px solid var(--nb-ink)', background: '#e8e3d5' }}
            >
              <img
                src={pet.gallery![currentImageIndex]}
                alt={pet.name}
                className="h-full w-full object-cover"
              />
              {pet.gallery!.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    aria-label="Imagen anterior"
                    className="nb-btn nb-btn-sm fill-paper absolute left-2 top-1/2 -translate-y-1/2 px-2 py-2"
                  >
                    <ChevronLeft size={14} />
                  </button>
                  <button
                    onClick={nextImage}
                    aria-label="Siguiente imagen"
                    className="nb-btn nb-btn-sm fill-paper absolute right-2 top-1/2 -translate-y-1/2 px-2 py-2"
                  >
                    <ChevronRight size={14} />
                  </button>
                  <div
                    className="absolute bottom-2 right-2 px-2 py-1 text-[10px] font-bold"
                    style={{
                      fontFamily: '"JetBrains Mono", monospace',
                      background: 'var(--nb-ink)',
                      color: 'var(--nb-paper)',
                    }}
                  >
                    {currentImageIndex + 1} / {pet.gallery!.length}
                  </div>
                </>
              )}
            </div>
          )}

          <div className="p-4">
            <div className="flex items-baseline justify-between gap-2">
              <h1 className="font-display text-[36px] leading-[0.9]">{pet.name}</h1>
              <NbChip fill="paper">{ANIMAL_TYPES[pet.type]}</NbChip>
            </div>

            {/* Basic details */}
            <div className="mt-3 grid grid-cols-2 gap-2">
              <DetailCell label="Edad" value={ANIMAL_AGES[pet.age]} />
              <DetailCell label="Sexo" value={PET_GENDER[pet.gender]} />
              <DetailCell label="Raza" value={pet.race || '—'} />
              <DetailCell
                label="Color"
                value={PET_COLORS[pet.main_color] ?? pet.main_color ?? '—'}
              />
            </div>

            {/* Health badges */}
            {(pet.is_sterilized || pet.is_vaccinated || pet.is_microchipped) && (
              <div className="mt-4 flex flex-wrap gap-2">
                {pet.is_sterilized && (
                  <NbChip fill="solved">
                    <Shield size={10} /> Esterilizado
                  </NbChip>
                )}
                {pet.is_vaccinated && (
                  <NbChip fill="found">
                    <Syringe size={10} /> Vacunado
                  </NbChip>
                )}
                {pet.is_microchipped && (
                  <NbChip fill="lost">
                    <Microchip size={10} /> Microchip
                    {pet.microchip_number ? ` · ${pet.microchip_number}` : ''}
                  </NbChip>
                )}
              </div>
            )}

            {/* Medical notes */}
            {pet.medical_notes && (
              <div
                className="fill-lost mt-4 p-3"
                style={{ border: '2px solid var(--nb-ink)', borderRadius: 'var(--nb-radius)' }}
              >
                <div
                  className="text-[10px] font-bold uppercase tracking-[0.08em]"
                  style={{ fontFamily: '"JetBrains Mono", monospace' }}
                >
                  / notas médicas
                </div>
                <p className="mt-1 text-[13px] font-medium leading-snug">{pet.medical_notes}</p>
              </div>
            )}
          </div>
        </NbCard>
      </div>

      {/* Contact */}
      <div className="px-4 pt-4">
        <NbCard className="fill-ink p-4 text-white">
          <div
            className="text-[10px] font-bold uppercase tracking-[0.08em] opacity-70"
            style={{ fontFamily: '"JetBrains Mono", monospace' }}
          >
            / contacto responsable
          </div>
          <div
            className="font-display mt-1 text-[22px] leading-tight"
            style={{ color: 'var(--nb-paper)' }}
          >
            ¿Encontraste a {pet.name}?
          </div>
          <div className="mt-2 text-[13px] opacity-90">
            Contactá a la familia directamente.
          </div>

          <div className="mt-3 flex flex-col gap-2">
            {pet.owner?.first_name && (
              <ContactRow icon={<User size={14} />} label="Dueño">
                {pet.owner.first_name} {pet.owner.last_name}
              </ContactRow>
            )}
            {pet.owner?.email && (
              <ContactRow icon={<Mail size={14} />} label="Email">
                <a className="underline decoration-2 underline-offset-2" href={`mailto:${pet.owner.email}`}>
                  {pet.owner.email}
                </a>
              </ContactRow>
            )}
            {pet.owner?.phone_number && (
              <ContactRow icon={<Phone size={14} />} label="Teléfono">
                <a className="underline decoration-2 underline-offset-2" href={`tel:${pet.owner.phone_number}`}>
                  {pet.owner.phone_number}
                </a>
              </ContactRow>
            )}
            {pet.owner?.address && (
              <ContactRow icon={<MapPin size={14} />} label="Dirección">
                {pet.owner.address}
              </ContactRow>
            )}
          </div>

          {(pet.owner?.email || pet.owner?.phone_number) && (
            <div className="mt-4 grid grid-cols-2 gap-2">
              {pet.owner?.email && (
                <NbButton
                  fill="lost"
                  onClick={() => window.open(`mailto:${pet.owner.email}`, '_blank')}
                >
                  <Mail size={14} /> Email
                </NbButton>
              )}
              {pet.owner?.phone_number && (
                <NbButton
                  fill="solved"
                  onClick={() => window.open(`tel:${pet.owner.phone_number}`, '_blank')}
                >
                  <Phone size={14} /> Llamar
                </NbButton>
              )}
            </div>
          )}
        </NbCard>
      </div>

      <div className="px-4 pt-4">
        <div
          className="py-3 text-center text-[10px] opacity-70"
          style={{ fontFamily: '"JetBrains Mono", monospace' }}
        >
          PERFIL GENERADO POR{' '}
          <a className="font-bold underline" href="https://rescatapp.com.ar">
            RESCATAPP.COM.AR
          </a>
        </div>
      </div>
    </div>
  );
};

const DetailCell: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div
    className="px-3 py-2"
    style={{ border: '2px solid var(--nb-ink)', borderRadius: 'var(--nb-radius)' }}
  >
    <div
      className="text-[9px] font-bold uppercase tracking-[0.08em] opacity-60"
      style={{ fontFamily: '"JetBrains Mono", monospace' }}
    >
      {label}
    </div>
    <div className="mt-0.5 break-words text-[13px] font-semibold">{value}</div>
  </div>
);

const ContactRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}> = ({ icon, label, children }) => (
  <div
    className="flex items-start gap-2 p-2"
    style={{ border: '2px solid var(--nb-paper)', borderRadius: 'var(--nb-radius)' }}
  >
    <div className="fill-lost mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center">
      {icon}
    </div>
    <div className="min-w-0 flex-1 text-[13px]">
      <div
        className="text-[9px] font-bold uppercase tracking-[0.08em] opacity-60"
        style={{ fontFamily: '"JetBrains Mono", monospace' }}
      >
        {label}
      </div>
      <div className="break-words font-semibold">{children}</div>
    </div>
  </div>
);
